import React from 'react'

import { CustomSpineEvents, DrawingSpineOptions, DrawingSpineOptionsEvent } from '../../../types/shared'
import ElementCanvas from '../../canvas/element'
import { getCanvasElementByType } from '../../helpers/get-canvas-element-by-type'
import ChartModel from '../../models/chart'
import ChartLayout from '../../models/chart_layout'
import { AutoSaveState } from '../../models/constants'
import Element from '../../models/element'
import { getUuid } from '../../utils/helpers'
import { compareHlc, incrementHlc, initHlc, receiveHlc, serializeHlc } from '../../utils/hlc'
import { usePrevious } from '../../utils/use-prev-state'
import {
  AutoSaveChangeType,
  AutoSaveElement,
  DRAWINGS_SAVE_INTERVAL_MS,
  DRAWINGS_UPDATE_INTERVAL_MS,
} from './constants'
import { useDrawingAutoSaveApi } from './use-drawing-autosave-api'
import { useIsAutoSaveActive } from './use-is-auto-save-active'
import {
  getContainerType,
  getInitialStoreDrawings,
  getTicker,
  getTickersAndIndicatorsInLayoutModel,
  mergeUpdatedDrawingsToInternalStore,
  updateInternalStoreWithChangedElement,
} from './utils'

function useDrawingAutoSave(chartLayout: ChartLayout) {
  const { fetchDrawings, saveDrawings, deleteAllDrawings } = useDrawingAutoSaveApi()
  const isAutoSaveActive = useIsAutoSaveActive(chartLayout)
  const prevIsAutoSaveActive = usePrevious(isAutoSaveActive)
  const internalStoreRef = React.useRef<AutoSaveElement[]>([])
  const instanceUUIDRef = React.useRef(getUuid())
  const latestRemoteUpdateTimestampRef = React.useRef(initHlc(instanceUUIDRef.current, 0))
  const lastestLocalUpdateTimestampRef = React.useRef(initHlc(instanceUUIDRef.current, 0))
  const saveTimeoutRef = React.useRef<NodeJS.Timeout>()

  const saveLatestChanges = React.useCallback(
    (withDelayInMs = DRAWINGS_SAVE_INTERVAL_MS) => {
      chartLayout.setAutoSaveState(AutoSaveState.UnsavedDrawings)
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      const saveQueue = internalStoreRef.current.filter(
        (item) =>
          compareHlc(item.lastChange, lastestLocalUpdateTimestampRef.current) > 0 &&
          item.lastChange.uuid === instanceUUIDRef.current
      )
      const shouldSkipDelay = saveQueue.some((item) => item.changeType === 'destroy')

      saveTimeoutRef.current = setTimeout(
        async () => {
          chartLayout.setAutoSaveState(AutoSaveState.SavingDrawings)
          const result = await saveDrawings(saveQueue)
          chartLayout.setAutoSaveState(AutoSaveState.SavedDrawings)
          if (result) {
            lastestLocalUpdateTimestampRef.current = receiveHlc(lastestLocalUpdateTimestampRef.current, result)
          }
        },
        shouldSkipDelay ? 0 : withDelayInMs
      )
    },
    [saveDrawings, chartLayout]
  )

  const syncChartLayoutDrawings = React.useCallback(
    (drawing: AutoSaveElement) => {
      const isRemove = drawing.changeType === 'destroy'
      chartLayout.getAllElements().forEach((element) => {
        if (element.isDrawing()) {
          const ticker = element.pane()?.chart()?.ticker

          const drawing = internalStoreRef.current.find(
            (drawing) =>
              drawing.elementId === element.elementId &&
              drawing.ticker === ticker &&
              drawing.containerType === getContainerType(element.pane())
          )
          if (!drawing || drawing.changeType === 'destroy') {
            element.destroyCascade()
          }
        }
      })

      chartLayout.getAllPanes().forEach((paneModel) => {
        const paneIndicatorType = getContainerType(paneModel)
        if (!paneIndicatorType || drawing.ticker !== paneModel.chart().quote()?.getRawTicker()) {
          return
        }
        const instanceObj = JSON.parse(drawing.elementAttrs)
        const element = paneModel
          .getAllElements()
          .find(
            (element) =>
              element.isDrawing() && element.elementId === drawing.elementId && element.pane()?.id === paneModel.id
          )

        const hasDrawingPaneIndicator = paneIndicatorType === drawing.containerType

        if (element) {
          if (drawing.elementAttrs !== JSON.stringify(element.instance.toAutosaveConfig())) {
            if (element.pane().selection !== element.instance) {
              element.instance.set(instanceObj)
              element.instance.updateScales()
            }
          }
          if (drawing.zIndex !== element.zIndex) {
            element.updateAttributes({ zIndex: drawing.zIndex })
          }
          if (isRemove || !hasDrawingPaneIndicator) {
            element.destroyCascade()
          }
        } else if (
          !isRemove &&
          !instanceObj.type.startsWith('charts/') &&
          getContainerType(paneModel) === drawing.containerType
        ) {
          const instance = getCanvasElementByType(instanceObj.type).fromObject(instanceObj, paneModel) as ElementCanvas
          paneModel.elements().create({ elementId: drawing.elementId, instance, zIndex: drawing.zIndex })
          instance.updateScales()
        }
      })
    },
    [chartLayout]
  )

  const checkDrawingUpdate = React.useCallback(
    async (isRefetchAll = false) => {
      const { tickers, containerTypes } = getTickersAndIndicatorsInLayoutModel(chartLayout)
      if (isRefetchAll) {
        latestRemoteUpdateTimestampRef.current = initHlc(instanceUUIDRef.current, 0)
      }

      const updatedDrawings = await fetchDrawings({
        tickers,
        containerTypes,
        lastChange: serializeHlc(latestRemoteUpdateTimestampRef.current),
      })
      const { newLastUpdateTimestamp, updatedElementIds, newInternalStore } = mergeUpdatedDrawingsToInternalStore({
        updatedDrawings,
        currentInternalStore: internalStoreRef.current,
        lastUpdateTimestamp: latestRemoteUpdateTimestampRef.current,
      })

      internalStoreRef.current = newInternalStore
      latestRemoteUpdateTimestampRef.current = newLastUpdateTimestamp
      if (isRefetchAll && internalStoreRef.current.length > 0) {
        saveLatestChanges(0)
        lastestLocalUpdateTimestampRef.current = receiveHlc(
          lastestLocalUpdateTimestampRef.current,
          [...internalStoreRef.current].sort((a, b) => compareHlc(b.lastChange, a.lastChange))[0].lastChange
        )
      }

      if (updatedElementIds.length > 0 || isRefetchAll) {
        const allElements = chartLayout.getAllElements()
        updatedElementIds.forEach((elementId) => {
          const element = allElements.find((element) => element.elementId === elementId)

          if (element && element.isDrawing() && !element.isMouseDown()) {
            element.destroyCascade()
          }
        })

        internalStoreRef.current.forEach(syncChartLayoutDrawings)
        chartLayout.getAllPanes().forEach((paneModel) => {
          paneModel.normalizeZIndexes()
        })
      }
    },
    [chartLayout, syncChartLayoutDrawings, fetchDrawings, saveLatestChanges]
  )

  const updateInternalStoreWithElement = React.useCallback(
    (element: Element, changeType: AutoSaveChangeType) => {
      const updateResult = updateInternalStoreWithChangedElement({
        element,
        changeType,
        currentInternalStore: internalStoreRef.current,
        lastUpdateTimestamp: incrementHlc(
          receiveHlc(lastestLocalUpdateTimestampRef.current, latestRemoteUpdateTimestampRef.current),
          Date.now()
        ),
      })

      if (updateResult) {
        const { shouldSyncAndSave, newInternalStore, newDrawing } = updateResult
        internalStoreRef.current = newInternalStore
        if (shouldSyncAndSave) {
          syncChartLayoutDrawings(newDrawing)
          saveLatestChanges()
        }
      }
    },
    [syncChartLayoutDrawings, saveLatestChanges]
  )

  const deleteAllAutoSavedElements = React.useCallback(async () => {
    const { tickers } = getTickersAndIndicatorsInLayoutModel(chartLayout)

    await deleteAllDrawings({ tickers, lastLocalChange: lastestLocalUpdateTimestampRef.current })
    await checkDrawingUpdate()
  }, [deleteAllDrawings, chartLayout, checkDrawingUpdate])

  const onElementChange = React.useCallback(
    (element: Element, _?: any, options?: DrawingSpineOptions) => {
      if (element.isDrawing() && !element.instance.isCreator) {
        chartLayout.getAllPanes().forEach((model) => {
          if (
            element.pane() &&
            getContainerType(model) === getContainerType(element.pane()) &&
            getTicker(model) === getTicker(element.pane())
          ) {
            const isRemove = options?.eventType === DrawingSpineOptionsEvent.Remove
            updateInternalStoreWithElement(element, isRemove ? 'destroy' : 'update')
          }
        })
      }
    },
    [updateInternalStoreWithElement, chartLayout]
  )

  React.useEffect(() => {
    // Restore initial drawings from server on page load
    const initialStoreDrawings = getInitialStoreDrawings()?.filter(({ containerType }) => containerType !== 'note')
    if (initialStoreDrawings?.length) internalStoreRef.current = initialStoreDrawings

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  React.useEffect(() => {
    if (!isAutoSaveActive) {
      chartLayout.setAutoSaveState(AutoSaveState.Off)
      return
    }
    chartLayout.setAutoSaveState(AutoSaveState.Saved)
    if (!prevIsAutoSaveActive && isAutoSaveActive) {
      checkDrawingUpdate(true)
    }

    let tickers: string[] = chartLayout.getAllCharts().map((chart) => chart.quote()?.getRawTicker() ?? '')

    const onChartChange = () => {
      const newTickers = chartLayout.getAllCharts().map((chart) => chart.quote()?.getRawTicker() ?? '')
      if (JSON.stringify(tickers) !== JSON.stringify(newTickers)) {
        tickers = newTickers
        checkDrawingUpdate(true)
      }
    }
    const onIndicatorChange = () => {
      checkDrawingUpdate(true)
    }

    ChartModel.bind('change', onChartChange)
    ChartModel.bind(CustomSpineEvents.IndicatorsChange, onIndicatorChange)
    Element.bind('change', onElementChange)

    chartLayout.getAllElements().forEach((element) => {
      // Adds drawings to internal store if isAutoSaveActive and they are not in the store yet
      if (!internalStoreRef.current.find((drawing) => drawing.elementId === element.elementId)) {
        onElementChange(element)
      }
    })

    internalStoreRef.current.forEach(syncChartLayoutDrawings)
    const checkDrawingUpdateInterval = setInterval(checkDrawingUpdate, DRAWINGS_UPDATE_INTERVAL_MS)

    return () => {
      clearInterval(checkDrawingUpdateInterval)
      ChartModel.unbind('change', onChartChange)
      ChartModel.unbind(CustomSpineEvents.IndicatorsChange, onIndicatorChange)
      Element.unbind('change', onElementChange)
    }
  }, [
    chartLayout,
    internalStoreRef,
    checkDrawingUpdate,
    onElementChange,
    syncChartLayoutDrawings,
    isAutoSaveActive,
    prevIsAutoSaveActive,
  ])

  return { deleteAllAutoSavedElements, isAutoSaveActive }
}

interface Props {
  chartLayoutModel: ChartLayout
}

export const withDrawingsAutoSave = (Component: any) => (props: Props) => {
  const { deleteAllAutoSavedElements, isAutoSaveActive } = useDrawingAutoSave(props.chartLayoutModel)

  return (
    <Component {...props} deleteAllAutoSavedElements={deleteAllAutoSavedElements} isAutoSaveActive={isAutoSaveActive} />
  )
}
